import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';

import { fuseAnimations } from '../../../../@fuse/animations';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { OrdersService } from '../../api/orders.service';
import { ProductsService } from '../../api/products.service';
import { UpsalePanelData, UpsalePanelService } from '../../services/upsale-panel.service';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';

interface UpsaleOptionTarget {
  type: 'product' | 'material';
  uuid: string;
  name: string;
  name_internal: string;
  price: number;
  price_label: string;
}

interface UpsaleOption {
  uuid: string;
  name: string;
  target: UpsaleOptionTarget;
  adding?: boolean;
  added?: boolean;
}

interface UpsaleGroup {
  uuid: string;
  name: string;
  type: string;
  options: UpsaleOption[];
}

interface UpsaleQuestion {
  uuid: string;
  name: string;
  step: number;
  groups: UpsaleGroup[];
}

/**
 * Upsale interne — contenu du side panel 'upsalePanel' (hosté dans layout-1,
 * même mécanique que addProductPanel). Charge l'arbre upsale du produit et
 * quick-add les cibles (produit → products/attach, matériel → materials/attach).
 * L'attach REMPLACE la qty côté backend, donc on envoie toujours qty existante+1.
 */
@Component({
  selector: 'app-order-upsale-panel',
  templateUrl: './order-upsale-panel.component.html',
  styleUrls: ['./order-upsale-panel.component.scss'],
  animations: fuseAnimations,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
})
export class OrderUpsalePanelComponent implements OnInit {
  questions: UpsaleQuestion[] = [];
  isLoading: boolean = false;
  productName: string = '';

  private data: UpsalePanelData | null = null;
  /** Dernier payload de commande retourné par un attach — remis à l'appelant à la fermeture. */
  private lastOrderData: any | false = false;

  constructor(
    private panelService: UpsalePanelService,
    private translationLoader: FuseTranslationLoaderService,
    public translate: TranslateService,
    private productsService: ProductsService,
    private ordersService: OrdersService,
    private toastr: ToastrService,
    private logger: NGXLogger,
  ) {
    this.translationLoader.loadTranslations(english, french);
    this.data = this.panelService.getData();
    this.productName = this.data?.productName ?? '';
  }

  ngOnInit(): void {
    if (!this.data?.productUUID) {
      return;
    }

    this.isLoading = true;
    this.productsService.getUpsalePanel(this.data.productUUID).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.questions = response?.data ?? [];
      },
      error: (error: any) => {
        this.isLoading = false;
        this.logger.error(
          'OrderUpsalePanelComponent.ngOnInit() -> ERROR: ' + JSON.stringify(error),
        );
        this.toastr.error('', this.translate.instant('ORDER_UPSALE.ADD_ERROR'));
      },
    });
  }

  public hasAnyOption(): boolean {
    return this.questions.some((question) =>
      question.groups.some((group) => group.options.length > 0),
    );
  }

  public onAddOption(option: UpsaleOption): void {
    if (option.adding || !this.data) {
      return;
    }

    option.adding = true;
    const target = option.target;

    if (target.type === 'material') {
      const qty = (this.data.materialQtyByUuid[target.uuid] ?? 0) + 1;
      this.ordersService
        .addMaterial(this.data.orderUUID, {
          materials: [{ uuid: target.uuid, qty: qty }],
        })
        .subscribe({
          next: (response: any) => {
            this.data!.materialQtyByUuid[target.uuid] = qty;
            this.onAddSuccess(option, response);
          },
          error: (error: any) => this.onAddError(option, error),
        });
      return;
    }

    const qty = (this.data.productQtyByUuid[target.uuid] ?? 0) + 1;
    this.ordersService
      .addProduct(this.data.orderUUID, {
        products: [{ uuid: target.uuid, qty: qty }],
      })
      .subscribe({
        next: (response: any) => {
          this.data!.productQtyByUuid[target.uuid] = qty;
          this.onAddSuccess(option, response);
        },
        error: (error: any) => this.onAddError(option, error),
      });
  }

  public close(): void {
    this.panelService.emitResult(this.lastOrderData);
  }

  private onAddSuccess(option: UpsaleOption, response: any): void {
    option.adding = false;
    option.added = true;
    this.lastOrderData = response?.data ?? this.lastOrderData;
    this.toastr.success('', this.translate.instant('ORDER_UPSALE.ADDED'));
  }

  private onAddError(option: UpsaleOption, error: any): void {
    option.adding = false;
    this.logger.error(
      'OrderUpsalePanelComponent.onAddOption() -> ERROR: ' + JSON.stringify(error),
    );
    this.toastr.error('', this.translate.instant('ORDER_UPSALE.ADD_ERROR'));
  }
}
